import { type RefObject, useCallback } from 'react';

interface AnchorJumpDeps {
  scrollRef: RefObject<HTMLElement | null>;
}

interface ScrollToSelectorOptions {
  behavior?: 'auto' | 'smooth';
  /** 让 selector 命中元素离视口顶部留出此偏移（默认 0 = 完全贴顶） */
  topOffsetPx?: number;
}

interface PinSubTargetOptions extends ScrollToSelectorOptions {
  /** 调用时机若 shouldAbort 命中，则跳过本次 pin。用于让 MessageList 把自身 userInterrupted/jumpToken 灌进来 */
  shouldAbort?: () => boolean;
}

interface WatchSubTargetOptions {
  watchMs: number;
  /** subTarget 距视口顶部的偏移绝对值 ≥ 此阈值才重写 scrollTop，避免与 measure 微动互相纠缠 */
  realignPx: number;
  shouldAbort?: () => boolean;
  /** watch 真正结束时调用一次。四种触发：watchMs 到期、shouldAbort 命中、scrollEl 卸载、selector 命中失败。
   *  外部调返回的 cancel handle 也会走这个。idempotent，多次调用也只触发一次。 */
  onDone?: () => void;
}

/** 把 sub 元素离视口顶部 topOffsetPx 处的 scrollTop 解出来，并 clamp 到 [0, maxScroll]。
 *  这是 anchor 跳转的几何核心：所有 selector → scrollTop 都过它，避免多处实现散落。 */
const computeTargetTop = (scrollEl: HTMLElement, sub: HTMLElement, topOffsetPx = 0): number => {
  const containerRect = scrollEl.getBoundingClientRect();
  const subRect = sub.getBoundingClientRect();
  const maxScroll = Math.max(0, scrollEl.scrollHeight - scrollEl.clientHeight);
  return Math.min(Math.max(0, subRect.top - containerRect.top + scrollEl.scrollTop - topOffsetPx), maxScroll);
};

/** anchor 跳转工具集合：纯 DOM 操作，不持有自己的 abort/token 状态。
 *  - ShareChat 直接 smooth scroll 一次到位
 *  - MessageList 在 virtualizer converge 完成后调 pinSubTargetToTop + watchSubTarget 兜图片/视频懒加载 */
export const useAnchorJump = ({ scrollRef }: AnchorJumpDeps) => {
  const scrollToSelector = useCallback(
    (selector: string, options?: ScrollToSelectorOptions): boolean => {
      const scrollEl = scrollRef.current;
      if (!scrollEl) return false;
      const sub = scrollEl.querySelector<HTMLElement>(selector);
      if (!sub) return false;
      const target = computeTargetTop(scrollEl, sub, options?.topOffsetPx);
      if (options?.behavior === 'smooth') {
        scrollEl.scrollTo({ top: target, behavior: 'smooth' });
      } else {
        scrollEl.scrollTop = target;
      }
      return true;
    },
    [scrollRef],
  );

  /** 一次性把 selector 命中元素贴齐视口顶部（同步 scrollTop 直写，无动画）。
   *  返回 true 表示对齐成功，调用方可视情况启动 watch 兜后续 measure 漂移。 */
  const pinSubTargetToTop = useCallback(
    (selector: string, options?: PinSubTargetOptions): boolean => {
      if (options?.shouldAbort?.()) return false;
      return scrollToSelector(selector, { behavior: 'auto', topOffsetPx: options?.topOffsetPx });
    },
    [scrollToSelector],
  );

  /** watchMs 内每帧重新计算 subTarget 与视口顶部的差，漂移超 realignPx 则重写 scrollTop。
   *  视频/图片懒加载会让 subTarget 在视口里持续漂移，靠这个 watch 兜住。
   *  返回 cancel handle，调用即等价于触发 onDone 并停止后续 RAF。 */
  const watchSubTarget = useCallback(
    (selector: string, options: WatchSubTargetOptions): (() => void) => {
      const watchStartedAt = Date.now();
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        options.onDone?.();
      };
      const tick = () => {
        if (done) return;
        if (options.shouldAbort?.()) return finish();
        const scrollEl = scrollRef.current;
        if (!scrollEl) return finish();
        const sub = scrollEl.querySelector<HTMLElement>(selector);
        if (!sub) return finish();
        const containerRect = scrollEl.getBoundingClientRect();
        const subRect = sub.getBoundingClientRect();
        const offsetFromTop = Math.round(subRect.top - containerRect.top);
        if (Math.abs(offsetFromTop) >= options.realignPx) {
          scrollEl.scrollTop = computeTargetTop(scrollEl, sub);
        }
        if (Date.now() - watchStartedAt < options.watchMs) {
          requestAnimationFrame(tick);
        } else {
          finish();
        }
      };
      requestAnimationFrame(tick);
      return finish;
    },
    [scrollRef],
  );

  return { scrollToSelector, pinSubTargetToTop, watchSubTarget };
};
